import * as PIXI from 'pixi.js';
import { HarnessNode as NodeData } from '../types';
import { BRAND, DIMENSIONS } from '../utils/constants';

/** Two left presses within this window count as a double-click (delete). */
const DOUBLE_CLICK_MS = 300;

export class HarnessNode extends PIXI.Container {
  private data: NodeData;
  private circle: PIXI.Graphics;
  private isSelected: boolean = false;
  /** Timestamp of the last left press, used to detect a double-click. */
  private lastLeftDownAt = 0;

  constructor(nodeData: NodeData) {
    super();
    
    this.data = nodeData;
    this.x = nodeData.xPosition;
    this.y = nodeData.yPosition;
    
    this.circle = new PIXI.Graphics();
    this.setupVisuals();
    this.setupInteraction();
  }

  private setupVisuals(): void {
    this.updateVisuals();
    this.addChild(this.circle);
  }

  private updateVisuals(): void {
    this.circle.clear();
    
    const radius = this.isSelected ? DIMENSIONS.NODE_RADIUS_SELECTED : DIMENSIONS.NODE_RADIUS;
    const color = this.isSelected ? BRAND.GOLD : BRAND.GREY_GREEN; // Gold when selected, grey-green otherwise
    
    this.circle.circle(0, 0, radius);
    this.circle.fill(color);
    this.circle.stroke({ width: 2, color: 0x2d3436 });
  }

  private setupInteraction(): void {
    this.eventMode = 'static';
    this.cursor = 'grab';

    // Ensure the node has a hit area for interaction
    const radius = DIMENSIONS.NODE_RADIUS_SELECTED; // Use larger radius for hit area
    this.hitArea = new PIXI.Circle(0, 0, radius);
    
    let isDragging = false;
    let dragOffset = { x: 0, y: 0 };
    
    this.on('pointerdown', (event) => {
      if (event.button === 2) { // Right click
        // The context menu is resolved centrally in the canvas handler (which
        // hit-tests nodes), so the node only needs to avoid starting a drag.
        return;
      }

      if (event.button === 1) { // Middle click - allow viewport panning
        return; // Don't prevent propagation, let InteractionManager handle it
      }
      
      // Only handle left mouse button for dragging
      if (event.button !== 0) return;

      // A quick second left press deletes the node instead of dragging it.
      const now = Date.now();
      const isDoubleClick = now - this.lastLeftDownAt < DOUBLE_CLICK_MS;
      this.lastLeftDownAt = now;
      if (isDoubleClick) {
        event.stopPropagation();
        this.emit('doubleClick');
        return;
      }

      isDragging = true;
      
      // Calculate offset from node position to mouse click in local coordinates
      const localPos = this.toLocal(event.global);
      dragOffset.x = localPos.x;
      dragOffset.y = localPos.y;
      
      this.emit('dragStart');
      event.stopPropagation(); // Only stop propagation for left mouse drag
      
      // Prevent any canvas mouse events during drag
      const canvas = this.getCanvas();
      if (canvas) {
        canvas.style.pointerEvents = 'none';
      }
      document.body.style.cursor = 'grabbing';

      // Set up global mouse tracking using DOM events
      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        
        
        // Get canvas and convert screen coordinates to world coordinates
        const canvas = this.getCanvas();
        if (!canvas) return;
        
        const rect = canvas.getBoundingClientRect();
        const screenX = e.clientX - rect.left;
        const screenY = e.clientY - rect.top;
        
        // Convert screen to world coordinates using parent (viewport) transform
        if (this.parent) {
          const worldPos = this.parent.toLocal({ x: screenX, y: screenY });
          this.x = worldPos.x - dragOffset.x;
          this.y = worldPos.y - dragOffset.y;
          this.updateData();
        }
      };
      
      const handleMouseUp = () => {
        isDragging = false;
        this.emit('dragEnd');
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        
        // Restore canvas mouse events
        const canvas = this.getCanvas();
        if (canvas) {
          canvas.style.pointerEvents = 'auto';
        }
        document.body.style.cursor = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    });
  }

  private updateData(): void {
    this.data.xPosition = this.x;
    this.data.yPosition = this.y;
    this.emit('positionChanged', this.x, this.y);
  }

  setSelected(selected: boolean): void {
    this.isSelected = selected;
    this.updateVisuals();
  }

  getData(): NodeData {
    return { ...this.data };
  }

  isPointInside(x: number, y: number): boolean {
    const dx = x - this.x;
    const dy = y - this.y;
    const radius = this.isSelected ? DIMENSIONS.NODE_RADIUS_SELECTED : DIMENSIONS.NODE_RADIUS;
    return (dx * dx + dy * dy) <= (radius * radius);
  }

  private getGlobalStage(): PIXI.Container | null {
    let current: PIXI.Container | null = this.parent;
    while (current && current.parent) {
      current = current.parent;
    }
    return current;
  }

  private getCanvas(): HTMLCanvasElement | null {
    // Find the canvas element by traversing up to find the renderer
    const stage = this.getGlobalStage();
    if (stage && (stage as any).renderer) {
      return (stage as any).renderer.canvas;
    }
    
    // Fallback: look for canvas in document
    const canvases = document.getElementsByTagName('canvas');
    if (canvases.length > 0) {
      return canvases[0];
    }
    
    return null;
  }
}